// /apps/patient/src/components/programs/DaySwapModal.tsx
import { useState } from 'react';
import { X, ArrowRight, Calendar, AlertCircle, RefreshCw, Check } from 'lucide-react';

// ISO day names lookup (1=Monday, 7=Sunday)
const ISO_DAY_NAMES: { [key: number]: string } = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday'
};

const ISO_DAY_SHORT: { [key: number]: string } = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun'
};

interface ScheduledWorkout {
  day: number; // ISO day (1-7)
  prescriptionName: string;
  isSwapped?: boolean;
}

interface DaySwapModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (originalDay: number, newDay: number) => Promise<void>;
  currentDay: number; // ISO day (1-7) of the workout being moved
  currentWorkoutName: string;
  weekNumber: number;
  scheduledWorkouts: ScheduledWorkout[]; // All scheduled workouts for the week
  loading?: boolean;
}

export function DaySwapModal({
  isOpen,
  onClose,
  onConfirm,
  currentDay,
  currentWorkoutName,
  weekNumber,
  scheduledWorkouts,
  loading = false
}: DaySwapModalProps) {
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  // Get which days already have workouts scheduled
  const scheduledDays = new Set(scheduledWorkouts.map(w => w.day));

  // Get available days (days without workouts in the same week)
  const availableDays = [1, 2, 3, 4, 5, 6, 7].filter(day =>
    day !== currentDay && !scheduledDays.has(day)
  );

  // Get workout name for a day (if any)
  const getWorkoutForDay = (day: number): ScheduledWorkout | undefined => {
    return scheduledWorkouts.find(w => w.day === day);
  };

  const handleConfirm = async () => {
    if (selectedDay === null) return;

    setError(null);
    setIsSubmitting(true);

    try {
      await onConfirm(currentDay, selectedDay);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to swap days. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDaySelect = (day: number) => {
    if (day === currentDay || isSubmitting) return;
    setSelectedDay(day);
    setError(null);
  };

  const isSwapTarget = selectedDay !== null && scheduledDays.has(selectedDay);
  const targetWorkout = selectedDay ? getWorkoutForDay(selectedDay) : null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-primary" />
              Swap Workout Day
            </h3>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Current Day Info */}
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-600 font-medium">Moving from:</p>
              <p className="text-lg font-semibold text-blue-900">
                {ISO_DAY_NAMES[currentDay]} - Week {weekNumber}
              </p>
              <p className="text-sm text-blue-700">{currentWorkoutName}</p>
            </div>

            {/* Day Selection */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Select new day:</p>

              <div className="grid grid-cols-7 gap-1">
                {[1, 2, 3, 4, 5, 6, 7].map(day => {
                  const workout = getWorkoutForDay(day);
                  const isCurrent = day === currentDay;
                  const isSelected = day === selectedDay;
                  const hasWorkout = workout && !isCurrent;

                  return (
                    <button
                      key={day}
                      onClick={() => handleDaySelect(day)}
                      disabled={isCurrent || isSubmitting}
                      className={`
                        p-2 rounded-lg text-center transition-all flex flex-col items-center
                        ${isCurrent
                          ? 'bg-blue-100 text-blue-400 cursor-not-allowed'
                          : isSelected
                            ? 'bg-primary text-white ring-2 ring-primary ring-offset-2'
                            : hasWorkout
                              ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }
                      `}
                    >
                      <span className="text-xs font-medium">{ISO_DAY_SHORT[day]}</span>
                      {hasWorkout && (
                        <RefreshCw className="h-3 w-3 mt-1" />
                      )}
                      {isCurrent && (
                        <span className="text-xs mt-1">Current</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gray-50 border rounded mr-1" />
                  Available
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-yellow-50 border border-yellow-200 rounded mr-1" />
                  Swap (has workout)
                </div>
              </div>
            </div>

            {/* Swap Preview */}
            {selectedDay !== null && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="text-center">
                    <p className="text-xs text-gray-500">From</p>
                    <p className="font-medium">{ISO_DAY_SHORT[currentDay]}</p>
                    <p className="text-xs text-gray-600 truncate max-w-[80px]">
                      {currentWorkoutName}
                    </p>
                  </div>

                  <ArrowRight className="h-5 w-5 text-gray-400 mx-2" />

                  <div className="text-center">
                    <p className="text-xs text-gray-500">To</p>
                    <p className="font-medium text-primary">{ISO_DAY_SHORT[selectedDay]}</p>
                    {isSwapTarget && targetWorkout ? (
                      <p className="text-xs text-yellow-600 truncate max-w-[80px]">
                        {targetWorkout.prescriptionName}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-400">Rest day</p>
                    )}
                  </div>
                </div>

                {isSwapTarget && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-start gap-2 text-sm text-yellow-700">
                      <RefreshCw className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>{targetWorkout?.prescriptionName}</strong> will move to {ISO_DAY_NAMES[currentDay]}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 text-red-700 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-lg">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedDay === null || isSubmitting}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Swapping...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Confirm Swap
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
